// Lectura de la máquina de estados por tipo de ticket.
// La definición vive en el backend (config/stateMachines.js) y llega por GET /state-machines.
// Aquí solo está lo visual: color y umbrales de cada estado.

const norm = (value) =>
  (value || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

const has = (list, state) => (list || []).some(s => norm(s) === norm(state));

// Se usa mientras carga la definición o si el backend no responde
const FALLBACK = {
  states: ['Recepcion', 'Clasificacion', 'Diagnostico', 'En progreso', 'En Espera', 'Resuelto', 'Cerrado', 'Cancelado'],
  initial: 'Recepcion',
  waiting: ['En Espera'],
  resolved: ['Resuelto'],
  final: ['Cerrado', 'Cancelado']
};

// Un color por estado, para todas las gráficas
export const STATE_STYLE = {
  'Recepcion':     { color: '#60A5FA', bg: 'rgba(96, 165, 250, 0.8)' },
  'Clasificacion': { color: '#8B5CF6', bg: 'rgba(139, 92, 246, 0.8)' },
  'Diagnostico':   { color: '#F97316', bg: 'rgba(249, 115, 22, 0.8)' },
  'En progreso':   { color: '#EAB308', bg: 'rgba(234, 179, 8, 0.8)' },
  'En Espera':     { color: '#9CA3AF', bg: 'rgba(156, 163, 175, 0.8)' },
  'En revisión':   { color: '#14B8A6', bg: 'rgba(20, 184, 166, 0.8)' },
  'Resuelto':      { color: '#22C55E', bg: 'rgba(34, 197, 94, 0.8)' },
  'Cerrado':       { color: '#16A34A', bg: 'rgba(22, 163, 74, 0.8)' },
  'Gestionado':    { color: '#16A34A', bg: 'rgba(22, 163, 74, 0.8)' },
  'Cancelado':     { color: '#6B7280', bg: 'rgba(107, 114, 128, 0.8)' }
};
const DEFAULT_STYLE = { color: '#6B7280', bg: 'rgba(107, 114, 128, 0.8)' };

// Minutos a partir de los cuales un período en ese estado pasa a alerta / crítico
export const STATE_TIME_THRESHOLDS = {
  'Recepcion':     { warning: 15, critical: 30 },
  'Clasificacion': { warning: 20, critical: 45 },
  'Diagnostico':   { warning: 30, critical: 60 },
  'En progreso':   { warning: 60, critical: 120 },
  'En revisión':   { warning: 60, critical: 120 },
  'En Espera':     { warning: 120, critical: 240 },
  'Resuelto':      { warning: 240, critical: 480 },
  'Cerrado':       { warning: 480, critical: 960 },
  'Gestionado':    { warning: 480, critical: 960 },
  'Cancelado':     { warning: 480, critical: 960 }
};

const lookup = (table, state) => table[Object.keys(table).find(k => norm(k) === norm(state))];

export const styleFor = (state) => lookup(STATE_STYLE, state) || DEFAULT_STYLE;
export const thresholdsFor = (state) => lookup(STATE_TIME_THRESHOLDS, state) || null;

/** Máquina del tipo. Sin tipo, o con un tipo desconocido, la estándar. */
export function machineFor(machines, type) {
  if (!machines) return FALLBACK;
  const key = Object.keys(machines.types || {}).find(t => norm(t) === norm(type));
  return key ? machines.types[key] : (machines.fallback || FALLBACK);
}

/** Estados que puede tener un ticket de ese tipo. Sin tipo seleccionado, todos. */
export function statesFor(machines, type) {
  if (!type) return (machines && machines.allStates) || FALLBACK.states;
  return machineFor(machines, type).states;
}

/** Estados en los que el ticket sigue en trabajo: ni resuelto ni final. */
export function activeStatesFor(machines, type) {
  if (!type) {
    const all = (machines && machines.allStates) || FALLBACK.states;
    const types = Object.values((machines && machines.types) || {});
    const done = types.length ? types.flatMap(t => [...t.final, ...t.resolved]) : [...FALLBACK.final, ...FALLBACK.resolved];
    return all.filter(s => !has(done, s));
  }
  const m = machineFor(machines, type);
  return m.states.filter(s => !has(m.final, s) && !has(m.resolved, s));
}

export const isFinalState = (machines, type, state) => has(machineFor(machines, type).final, state);

/** true si el ticket ya no está en trabajo (resuelto o final según su tipo). */
export function isDone(machines, ticket) {
  const m = machineFor(machines, ticket.type);
  return has(m.final, ticket.state_name) || has(m.resolved, ticket.state_name);
}

export const sameState = (a, b) => norm(a) === norm(b);
